import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowRight, Clock, GitBranch, Layers } from 'lucide-react';
import type { OpsKaizenData } from './useOpsKaizenData';
import { OpsKaizenTeamTasksPanel } from './OpsKaizenTeamTasksPanel';
import {
  CARD_TITLE_CLASS,
  CHART_BAR,
  CHART_GRID,
  CHART_TICK,
  EMPTY_OVERLAY_MSG,
  INDIGO_MAIN,
  KAIZEN_GAP,
  KAIZEN_STRETCH_PANEL,
  KAIZEN_STRETCH_ROW,
  KAIZEN_VIEWPORT_CLASS,
  PANEL_CLASS,
  PANEL_HEADER_COMPACT,
  RECHART_MARGIN,
  ChartBlock,
  PremiumTooltip,
} from './opsKaizenShared';
import {
  FLOW_HEALTH_BORDER,
  flowHealthFromMetrics,
  type FlowHealth,
} from '../executive/opsExecutiveShared';

interface OpsKaizenValueFlowTabProps {
  data: OpsKaizenData;
  onOpenBottlenecks?: () => void;
}

const HEALTH_LABEL: Record<FlowHealth, string> = {
  healthy: 'Saudável',
  attention: 'Atenção',
  bottleneck: 'Gargalo',
};

const HEALTH_DOT: Record<FlowHealth, string> = {
  healthy: 'bg-emerald-500',
  attention: 'bg-amber-500',
  bottleneck: 'bg-red-500',
};

export const OpsKaizenValueFlowTab: React.FC<OpsKaizenValueFlowTabProps> = ({ data, onOpenBottlenecks }) => (
  <div className={`flex min-h-0 flex-1 flex-col ${KAIZEN_GAP} ${KAIZEN_VIEWPORT_CLASS}`}>
    <div className={`${PANEL_CLASS} shrink-0 border-l-[3px] border-l-sky-500/70`}>
      <div className={PANEL_HEADER_COMPACT}>
        <div className="flex items-center gap-1.5">
          <GitBranch size={13} className="text-sky-600" strokeWidth={2.25} />
          <span className={CARD_TITLE_CLASS}>Fluxo Operacional Vivo</span>
        </div>
        <span className="text-[9px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
          WIP · lead time · SLA por etapa
        </span>
      </div>
      <div className="overflow-x-auto px-4 py-4">
        {data.hasOperationalFlow ? (
          <div className="flex min-w-max items-stretch gap-0">
            {data.operationalStages.map((stage, index) => {
              const health = flowHealthFromMetrics(stage.wip, stage.slaRate, stage.isBottleneck, stage.slaSampled);
              const displayHealth: FlowHealth = stage.isBottleneck ? 'bottleneck' : health;
              const StageTag = stage.isBottleneck && onOpenBottlenecks ? 'button' : 'div';
              return (
                <React.Fragment key={stage.name}>
                  {index > 0 && (
                    <div className="flex shrink-0 flex-col items-center justify-center px-1.5 text-sky-500/70">
                      <ArrowRight size={20} strokeWidth={2.5} />
                    </div>
                  )}
                  <StageTag
                    type={stage.isBottleneck && onOpenBottlenecks ? 'button' : undefined}
                    onClick={stage.isBottleneck ? onOpenBottlenecks : undefined}
                    className={`flex w-[min(200px,28vw)] shrink-0 flex-col rounded-sm border px-3 py-2.5 text-left ${FLOW_HEALTH_BORDER[displayHealth]} ${
                      stage.isBottleneck ? 'scale-[1.02] shadow-md' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-[12px] font-bold text-[var(--text-primary)]">{stage.name}</p>
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className={`h-2 w-2 rounded-full ${HEALTH_DOT[displayHealth]}`} />
                          <span className={`text-[9px] font-semibold uppercase tracking-wide ${stage.isBottleneck ? 'text-red-600' : 'text-[var(--text-secondary)]'}`}>
                            {stage.isBottleneck ? 'Gargalo principal' : HEALTH_LABEL[health]}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-1.5">
                      <div>
                        <p className="text-[8px] font-bold uppercase text-[var(--text-muted)]">WIP</p>
                        <p className={`text-[15px] font-bold tabular-nums ${displayHealth === 'bottleneck' ? 'text-red-500' : 'text-[var(--text-primary)]'}`}>
                          {Math.round(stage.wip)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[8px] font-bold uppercase text-[var(--text-muted)]">Lead Time</p>
                        <p className="text-[12px] font-semibold tabular-nums text-[var(--text-primary)]">{stage.cycle}</p>
                      </div>
                      <div>
                        <p className="text-[8px] font-bold uppercase text-[var(--text-muted)]">SLA</p>
                        <p className={`text-[12px] font-semibold tabular-nums ${!stage.slaSampled ? 'text-[var(--text-muted)]' : stage.slaRate >= 95 ? 'text-emerald-600' : stage.slaRate >= 85 ? 'text-amber-600' : 'text-red-500'}`}>
                          {stage.slaSampled ? `${stage.slaRate.toFixed(0)}%` : '—'}
                        </p>
                      </div>
                    </div>
                    {stage.isBottleneck && onOpenBottlenecks && (
                      <span className="mt-2 text-[9px] font-semibold text-[var(--nav-active-bar)]">Ver gargalo</span>
                    )}
                  </StageTag>
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div className="flex h-[72px] items-center justify-center rounded-sm border border-dashed border-[var(--border-default)]/50 bg-[var(--bg-elevated)]/25 px-2">
            <span className="text-[10px] font-medium text-[var(--text-muted)]">{EMPTY_OVERLAY_MSG}</span>
          </div>
        )}
      </div>
    </div>

    <div className={`flex min-h-0 flex-1 flex-col ${KAIZEN_GAP}`}>
      <div className={KAIZEN_STRETCH_ROW}>
        <ChartBlock
          title="WIP por Etapa"
          icon={<Layers size={13} className="text-sky-600" strokeWidth={2.25} />}
          subtitle={`Top ${Math.max(data.wipStageRows.length, 1)}`}
          hasActivity={data.hasWipStageData}
          fill
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={(data.hasWipStageData ? data.wipStageRows : [{ stage_name: '—', avg_wip: 0 }]) as typeof data.wipStageRows}
              layout="vertical"
              margin={RECHART_MARGIN.barVertical}
            >
              <CartesianGrid horizontal={false} stroke={CHART_GRID} strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, data.maxStageWip]} axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 9 }} />
              <YAxis type="category" dataKey="stage_name" axisLine={false} tickLine={false} width={84} tick={{ fill: CHART_TICK, fontSize: 9 }} />
              <Tooltip content={<PremiumTooltip formatter={(value) => ({ main: `${value} itens em WIP`, sub: '' })} />} />
              <Bar dataKey="avg_wip" fill={data.hasWipStageData ? CHART_BAR : 'transparent'} radius={[0, 2, 2, 0]} isAnimationActive animationDuration={1200}>
                <LabelList dataKey="avg_wip" position="right" offset={4} className="fill-[var(--text-secondary)]" fontSize={9} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartBlock>

        <div className={KAIZEN_STRETCH_PANEL}>
          <div className={PANEL_HEADER_COMPACT}>
            <div className="flex items-center gap-1.5">
              <Layers size={13} className="text-sky-600" strokeWidth={2.25} />
              <span className={CARD_TITLE_CLASS}>WIP por Etapa</span>
            </div>
            <span className="text-[9px] font-medium uppercase tracking-wider text-[var(--text-muted)]">Atual vs limite</span>
          </div>
          <div className="relative min-h-0 flex-1 overflow-hidden px-3 py-1">
            <table className="w-full border-collapse text-[10px]">
              <thead>
                <tr className="border-b border-[var(--border-default)]/60">
                  <th className="px-1 py-0.5 text-left font-medium text-[var(--text-muted)]">Etapa</th>
                  <th className="px-1 py-0.5 text-center font-medium text-[var(--text-muted)]">WIP</th>
                  <th className="px-1 py-0.5 text-center font-medium text-[var(--text-muted)]">Lim.</th>
                  <th className="px-1 py-0.5 text-center font-medium text-[var(--text-muted)]">St.</th>
                </tr>
              </thead>
              <tbody>
                {(data.hasStageWipTable ? data.stageWipRows.slice(0, 5) : [{ stageId: '—', stageName: '—', wipAtual: 0, wipLimite: null, overLimit: false }]).map((row) => (
                  <tr key={row.stageId} className={`border-b border-[var(--border-default)]/40 ${row.overLimit ? 'bg-red-500/5' : ''}`}>
                    <td className="max-w-[72px] truncate px-1 py-0.5 text-[var(--text-primary)]">{row.stageName}</td>
                    <td className="px-1 py-0.5 text-center font-semibold tabular-nums">{row.wipAtual}</td>
                    <td className="px-1 py-0.5 text-center text-[var(--text-secondary)]">{row.wipLimite ?? '—'}</td>
                    <td className="px-1 py-0.5 text-center">
                      <span className={`text-[9px] ${row.overLimit ? 'text-red-500' : 'text-emerald-600'}`}>
                        {row.overLimit ? '!' : 'OK'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!data.hasStageWipTable && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-[var(--bg-surface)]/50">
                <span className="text-[10px] font-medium text-[var(--text-muted)]">Sem dados de WIP</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={KAIZEN_STRETCH_ROW}>
        <ChartBlock
          title="Lead Time por Dia"
          icon={<Clock size={13} className="text-indigo-600" strokeWidth={2.25} />}
          subtitle="Média em horas"
          hasActivity={data.hasLeadTimeData}
          fill
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.leadTimeChartData} margin={RECHART_MARGIN.line}>
              <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.chartDays / 7) - 1)} tick={{ fill: CHART_TICK, fontSize: 9 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 9 }} width={32} tickFormatter={(v) => `${Math.round(Number(v))}h`} />
              <Tooltip content={<PremiumTooltip formatter={(v) => ({ main: `${Number(v).toFixed(1)}h`, sub: 'lead time médio' })} />} />
              <Line type="monotone" dataKey="value" stroke={INDIGO_MAIN} strokeWidth={1.5} dot={false} activeDot={{ r: 3, fill: INDIGO_MAIN }} isAnimationActive animationDuration={1200} />
            </LineChart>
          </ResponsiveContainer>
        </ChartBlock>

        <OpsKaizenTeamTasksPanel data={data} />
      </div>
    </div>
  </div>
);

export default OpsKaizenValueFlowTab;
